import { useContext, useEffect, useState } from "react"
import { RiCloseFill } from "react-icons/ri"
import ApiContext from "src/provider/API/call-service"
import { AbsoluteContainer, AppLabel, AppSpan, AppText, Button, CustomContainer, FlexColumn, FlexRow, Form, FormGroup, FormGroupWithIcon, ReactSelect, SidePopupContainer, Switch } from "src/style"
import helpers from "src/utils/helpers"
import UploadIcon from 'src/assets/img/upload-icon.svg'
import CloseIcon from 'src/assets/svg/close-icon'
import utility from "src/utils/utility"
import {useNotifier} from 'react-pop-notifier';
import { FiChevronDown } from "react-icons/fi"
import { ServiceCategory } from "src/model"
import { ColorPicker } from "src/component"

export const CreateProduct: React.FC<any> = ({serviceProviderId, fetchProducts, close}) => {
    const {notifier} = useNotifier()
    const {API} = useContext(ApiContext)
    const [input, setInput] = useState({
        name: '', features: '', description: '', websiteUrl: '',
        productOwner: '', packageName: '', ipAddress: '', port: '', productId: '',
        premium: '', feeShare: '', paymentFrequency: '', baseUrl: '', benefitAmount: '',
        tags: null, logo: '', usageGuide: '', termsAndConditions: '', setupFee: 0,
        configureSetupFee: false, tooltip: '', serviceCategoryId: '', integrationStatus: '',
        logoBgColor: ''
    })
    const [logoExtra, setLogoExtra] = useState({
        logoName: '', logoUrl: '',logoSize: '', logoExtension: '', logoContentType: '',
        screenshotName: '', screenshotUrl: '',screenshotSize: '', screenshotExtension: '',
        screenshotContentType: ''
    })
    const [screenshots, setScreenshots] = useState<Array<any>>([])
    const [isHover, setIsHover] = useState<any>({screenshot1: false})
    const [serviceCategories, setServiceCategories] = useState<Array<ServiceCategory>|null>(null)
    const [view, setView] = useState('')

    useEffect(() => {
        handleFetchServiceCategories()
    }, [])

    async function handleFetchServiceCategories () {
        const response = await API.getServiceCategoriesWithoutPagination()
        if (response) setServiceCategories(response)
    }

    const handleInput = (e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) => setInput({...input, [e.target.name]: e.target.value})

    async function handleFile (e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files instanceof FileList) {
            if(!helpers.isValidFileType(e.target.value)) return notifier.show('Only SVG, PNG, and JPEG files are allowed')

            const extension = helpers.getFileExtension(e.target.value)

            const uploadedFile = await helpers.compressImage(e.target.files[0])
            if (!uploadedFile) return notifier.show('Unable to compress large file size uploaded')

            helpers.convertFileToBase64(uploadedFile, true)
            .then(async (result: any) => {
                setInput(prevInput => ({...prevInput,
                    [e.target.name]: result.baseURL,
                }))

                const url = URL.createObjectURL(e.target.files![0])
                const size = (e.target.files![0]?.size / 1024).toFixed(1)
                const fileName = e.target.files![0]?.name

                if (e.target.name != 'screenshot') {
                    setLogoExtra(prevLogoExtra => ({
                        ...prevLogoExtra, 
                        [`${e.target.name}Name`]: fileName,
                        [`${e.target.name}Url`]: url,
                        [`${e.target.name}Size`]: size,
                        [`${e.target.name}Extension`]: extension,
                        [`${e.target.name}ContentType`]: result.contentType
                    }))
                }
                else {
                    const newScreenshot = {
                        name: `screenshot${screenshots.length + 1}`,
                        file: result.baseURL,
                        url,
                        fileName,
                        extension,
                        contentType: result.contentType
                    }
                    setScreenshots(prevScreenShots => [...prevScreenShots, newScreenshot])
                }
            })
            .catch(err => {
                notifier.show(JSON.stringify(err), 'Validation Error')
            })
        }
    }

    function handleRemoveScreenshot (name: string) {
        const allScreenshots = screenshots.filter(screenshot => screenshot.name != name)
        setScreenshots(allScreenshots)
    }

    async function handleSubmit (e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()

        if (!input.logoBgColor) return notifier.show('Kindly provide background color for product logo', 'Validation Error')

        const payload: any = {
            ...input, 
            serviceProviderId, 
            screenshots,
            logo: {
                file: input.logo,
                contentType: logoExtra.logoContentType,
                fileName: logoExtra.logoName
            }
        }

        if (!input.logo) delete payload.logo
        if (!screenshots.length) delete payload.screenshots
        delete payload.screenshot

        const response = await API.createProduct(payload)
        if (response) {
            fetchProducts()
            close()
        }
    }

    return (
        <>
            <SidePopupContainer zIndex="8" onClick={close} />
            <FlexColumn
                className="center-popup-container"
                justifyContent="flex-start"
                alignItems="flex-start" 
            >
                <FlexRow justifyContent='space-between' gap='2'>
                    <AppSpan color='#101828' textSize='1.8' fontWeight="600">Create New Product</AppSpan>
                    <Button 
                        titleSize="2" fontWeight="500"
                        width='3.4' shadow='none'
                        height='3.4'
                        radius='0.4'
                        bgColor='#ffffff'
                        hoverBgColor='#FEF3F2'
                        borderColor='none'
                        color='#000000'
                        hoverColor='#B42318'
                        onClick={close}
                    >
                        <RiCloseFill style={{fontSize: '2.3rem'}} />
                    </Button> 
                </FlexRow>
                <Form onSubmit={handleSubmit} topMargin='2'>
                    <CustomContainer bottomMargin="2">
                        <AppLabel color="#344054" fontWeight="500" bottomMargin="0.8">Logo</AppLabel>
                        { !input.logo ?
                            <FlexColumn 
                                radius='0.8' minHeight='12.6' 
                                padding='1.5' bgColor="#F7F8F9"
                            >
                                <img src={UploadIcon} />
                                <input 
                                    type="file" name='logo' alt='logoExtension' 
                                    onChange={handleFile} 
                                    className="logo-input" 
                                    id="logo-id" 
                                />
                                <AppLabel color='#0A756F' fontWeight="500" topMargin='1' htmlFor='logo-id' align='center'>
                                    Click to upload
                                </AppLabel>
                                <AppSpan textSize='1.2' topMargin='0.3'>SVG preferred</AppSpan>
                            </FlexColumn>
                            :
                            <CustomContainer 
                                borderColor='#0D968F' radius='0.8'
                                padding='1.5' minHeight='12.6'
                                bgColor='transparent'
                            >
                                <FlexRow 
                                    justifyContent='space-between'
                                    gap='2' bottomMargin='1'
                                >
                                    <CustomContainer width='6.8' height='3.1'>
                                        <img 
                                            src={logoExtra.logoUrl} className='img-covered' 
                                            style={{objectFit: 'contain', objectPosition: 'left'}} 
                                        />
                                    </CustomContainer>
                                    <AppSpan 
                                        hoverColor='#B42318' cursor='pointer'
                                        onClick={() => setInput({...input, logo: ''})}
                                    >
                                        <RiCloseFill fontSize='2rem' />
                                    </AppSpan>
                                </FlexRow>
                                <AppText fontWeight='600'>{logoExtra.logoName}</AppText>
                                { logoExtra.logoSize &&
                                    <AppSpan color='#667085'>{logoExtra.logoSize} KB</AppSpan>
                                }
                            </CustomContainer>
                        }
                    </CustomContainer>
                    <CustomContainer bottomMargin="2">
                        <AppLabel color="#344054" fontWeight="500" bottomMargin="0.8">Screenshots</AppLabel>
                        <FlexColumn 
                            radius='0.8' minHeight='12.6' 
                            padding='1.5' bgColor="#F7F8F9"
                        >
                            <img src={UploadIcon} />
                            <input 
                                type="file" name='screenshot'
                                onChange={handleFile} 
                                className="logo-input" 
                                id="screenshot-id" 
                            />
                            <AppLabel color='#0A756F' fontWeight="500" topMargin='1' htmlFor='screenshot-id' align='center'>
                                Click to upload
                            </AppLabel>
                            <AppSpan textSize='1.2' topMargin='0.3'>SVG preferred</AppSpan>
                        </FlexColumn>
                    </CustomContainer>
                    { screenshots.length > 0 &&
                        <FlexRow 
                            gap="1.5" bottomMargin="2"
                            className="scrollable-container"
                        >
                            {
                                screenshots?.map((screenshot, index) => {
                                    return (
                                        <CustomContainer 
                                            width="12.46" height="5.5" radius="0.5" bgColor="#F7F8F9"
                                            style={{flexBasis: 'auto', flexShrink: '0', flexGrow: '0'}} 
                                            key={index}
                                        >
                                            <AbsoluteContainer
                                                top="-0.2" right="0.2"
                                            >
                                                <CloseIcon
                                                    onMouseEnter={() => setIsHover({...isHover, [screenshot.name]: true})}
                                                    onMouseLeave={() => setIsHover({...isHover, [screenshot.name]: false})}
                                                    isHover={isHover[screenshot.name]}
                                                    onClick={() => handleRemoveScreenshot(screenshot.name)}
                                                />
                                            </AbsoluteContainer>
                                            <img src={screenshot.url} className='img-covered' style={{objectFit: 'cover'}} />
                                        </CustomContainer>
                                    )
                                })
                            }
                        </FlexRow>
                    }
                    <FormGroup>
                        <label>Product name</label>
                        <input 
                            placeholder="Enter product name" 
                            name='name'
                            value={input.name}
                            onChange={handleInput}
                            required
                        />
                    </FormGroup>  
                    <FormGroup>
                        <label>Product features</label>
                        <input 
                            placeholder="Enter product features" 
                            name='features'
                            value={input.features}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroupWithIcon>
                        <label>Product logo background color</label>
                        <div 
                            className="input-with-icon"
                            onClick={() => setView('color-picker')}
                        >
                            { input.logoBgColor ?
                                <p>{input.logoBgColor}</p>
                                : <p style={{color: '#667085', cursor: 'pointer'}}>Click to pick color</p>
                            }
                            <CustomContainer 
                                width='2.4' height='2.4' 
                                radius='0.3' bgColor={input.logoBgColor}
                                className="input-with-icon-tag" 
                            />
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon >
                        <label>Service Category</label>
                        <div className="input-with-icon" style={{height: '4rem'}}>
                            <select 
                                name='serviceCategoryId'
                                value={input.serviceCategoryId}
                                style={{color: input.serviceCategoryId ? '#000000' : '#667085'}}
                                onChange={handleInput}
                                autoComplete="off"
                            >
                                <option value="">Select category</option>
                                {
                                    serviceCategories?.map((item: ServiceCategory, index: number) => {
                                        return <option value={item.id} key={index}>{item.name}</option>
                                    })
                                }
                            </select>
                            <FiChevronDown className="input-with-icon-tag" />
                        </div>
                    </FormGroupWithIcon>
                    <FormGroup>
                        <label>Tooltip</label>
                        <input 
                            placeholder="Enter tooltip" 
                            name='tooltip'
                            value={input.tooltip}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Product description</label>
                        <textarea 
                            placeholder="Enter product description" 
                            name='description'
                            value={input.description}
                            onChange={handleInput}
                        />
                    </FormGroup>
                    <FlexRow gap="1.5" alignItems="flex-start" justifyContent="flex-start" bottomMargin="2">
                        <AppSpan fontWeight="600" color="#344054">Configure setup fee</AppSpan>
                        <Switch
                            radius='1.17' bgColor='#E7E7E7' 
                            checkedBgColor='#0D968F' checkedSliderBgColor="#ffffff"
                        >
                            <input 
                                type="checkbox" 
                                checked={input.configureSetupFee}
                                onChange={(e) => setInput({...input, configureSetupFee: e.target.checked})} 
                            />
                            <div ></div>
                            <span></span>
                        </Switch>
                    </FlexRow> 
                    { input.configureSetupFee &&
                        <FormGroup>
                            <label>Setup Fee</label>
                            <input 
                                placeholder="Enter setup fee" 
                                name='setupFee'
                                value={input.setupFee}
                                onChange={handleInput}
                                required
                            />
                        </FormGroup>  
                    }
                    <FormGroup>
                        <label>Product website url</label>
                        <input 
                            placeholder="Enter website url" 
                            name='websiteUrl'
                            value={input.websiteUrl}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <CustomContainer>
                        <AppLabel 
                            bottomMargin="0.8" fontWeight="500" color="#344054"
                        >
                            Tags
                        </AppLabel>
                        <ReactSelect 
                            bottomMargin="2"
                            styles={utility.reactSelectStyles()} 
                            options={
                                tags?.map((item: any, index: number) => {
                                    return {
                                        label: item.name,
                                        value: item.alias,
                                        key: index
                                    }
                            })
                            } 
                            isClearable={true} 
                            isSearchable={true}
                            name='tags' 
                            placeholder="Select Tags" 
                            isMulti
                            menuPortalTarget={document.body} 
                            value={input.tags}
                            onChange={(val: any) => setInput({...input, tags: val})}
                        />
                    </CustomContainer>
                    <FormGroup>
                        <label>Usage guide</label>
                        <textarea 
                            placeholder="Enter usage guide" 
                            name='usageGuide'
                            value={input.usageGuide}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Terms and conditions</label>
                        <textarea 
                            placeholder="Enter terms and conditions" 
                            name='termsAndConditions'
                            value={input.termsAndConditions}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroupWithIcon >
                        <label>Integration status</label>
                        <div className="input-with-icon" style={{height: '4rem'}}>
                            <select 
                                name='integrationStatus'
                                value={input.integrationStatus}
                                style={{color: input.integrationStatus ? '#000000' : '#667085'}}
                                onChange={handleInput}
                                autoComplete="off"
                            >
                                <option value="">--Select--</option>
                                <option value="AvailableAutomatically">Available Automatically</option>
                                <option value="AvailableManually">Available Manually</option>
                                <option value="ComingSoon">Coming Soon</option>
                            </select>
                            <FiChevronDown className="input-with-icon-tag" />
                        </div>
                    </FormGroupWithIcon>
                    {/* <FormGroup>
                        <label>Product owner</label>
                        <input 
                            placeholder="Enter product owner" 
                            name='productOwner'
                            value={input.productOwner}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Package name</label>
                        <input 
                            placeholder="Enter package name" 
                            name='packageName'
                            value={input.packageName}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>IP address</label>
                        <input 
                            placeholder="Enter IP address" 
                            name='ipAddress'
                            value={input.ipAddress}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Port</label>
                        <input 
                            placeholder="Enter Port" 
                            name='port'
                            value={input.port}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>ID</label>
                        <input 
                            placeholder="Enter product ID" 
                            name='productId'
                            value={input.productId}
                            onChange={handleInput}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>Premium</label>
                        <input 
                            placeholder="Enter premium" 
                            name='premium'
                            value={input.premium}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Payment frequency</label>
                        <input 
                            placeholder="Enter payment frequency" 
                            name='paymentFrequency'
                            value={input.paymentFrequency}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>FI fee share %</label>
                        <input 
                            placeholder="Enter fee share" 
                            name='feeShare'
                            value={input.feeShare}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Base url</label>
                        <input 
                            placeholder="Enter base url" 
                            name='baseUrl'
                            value={input.baseUrl}
                            onChange={handleInput}
                        />
                    </FormGroup> 
                    <FormGroup>
                        <label>Benefit amount</label>
                        <input 
                            placeholder="Enter benefit amount" 
                            name='benefitAmount'
                            value={input.benefitAmount}
                            onChange={handleInput}
                        />
                    </FormGroup>  */}

                    <FlexRow justifyContent='flex-end'>
                        <Button
                            width='8.1'
                            titleSize='1.4' fontWeight="500"
                            height='4' radius='0.8'
                            bottomMargin='1' hoverColor='#0D968F'
                            hoverBgColor='#fff' borderColor='#0D968F'
                        >
                            Submit
                        </Button>
                    </FlexRow>
                </Form>
            </FlexColumn>
            { view === 'color-picker' &&
                <ColorPicker 
                    close={() => setView('')}
                    output={(color: string) => setInput({...input, logoBgColor: color})}
                />
            }
        </>
    )
}

const tags = [
    {name: 'Banking', alias: 'banking'},
    {name: 'Finance', alias: 'finance'},
    {name: 'Qore', alias: 'qore'},
    {name: 'Money', alias: 'money'},
]